import type { ReactNode } from 'react';
import { GROUP_HEADER_CELL_BORDER_BOTTOM, PINNED_RIGHT_EDGE_LINE_STYLE } from '../../../../constants/borderStyles';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import { isEagColumnGroup } from '../../../../utils/isEagColumnGroup';
import { findFillerInsertIndex } from './utils/findFillerInsertIndex';
export type GroupHeaderRowProps = {
  columnConfig: EagColumnOrGroup[];
  visibleKeys: Set<string>;
  // When action column is enabled, add a matching empty <th> for structure
  actionColumnWidth?: number;
  // Number of non-right-pinned visible columns — used for filler <th> positioning
  nonRightPinnedCount: number;
};
export type RenderCell = {
  type: 'group';
  groupId: string;
  headerName: string;
  colSpan: number;
  positionKey: string;
} | {
  type: 'ungrouped';
  columnKey: string;
};

/**
 * Renders the group header <tr> that sits above the column header row.
 * Each EagColumnGroup becomes a <th colSpan={n}> where n is the number
 * of its children that are currently visible. Ungrouped columns get an
 * empty placeholder <th colSpan={1}>. Groups whose children are all
 * hidden are omitted entirely.
 *
 * The same groupId may appear multiple times in columnConfig when group members
 * are non-consecutive in the definition. Each occurrence is tracked with a
 * positional key (groupId + occurrence index) to avoid duplicate React keys.
 */
export function GroupHeaderRow(props: GroupHeaderRowProps) {
  // Build render cells in one pass, using a positional key to disambiguate
  // multiple occurrences of the same groupId.
  const groupOccurrences = new Map<string, number>();
  const renderCells: RenderCell[] = [];
  for (const item of props.columnConfig) {
    if (isEagColumnGroup(item)) {
      const visibleCount = item.children.filter(child => props.visibleKeys.has(child.key)).length;
      if (visibleCount === 0) {
        continue;
      }
      const occurrence = (groupOccurrences.get(item.groupId) ?? 0) + 1;
      groupOccurrences.set(item.groupId, occurrence);
      renderCells.push({
        type: 'group',
        groupId: item.groupId,
        headerName: item.headerName,
        colSpan: visibleCount,
        positionKey: `${item.groupId}-${occurrence}`
      });
    } else {
      if (!props.visibleKeys.has(item.key)) {
        continue;
      }
      renderCells.push({
        type: 'ungrouped',
        columnKey: item.key
      });
    }
  }

  // Split renderCells at the boundary where cumulative colSpan reaches nonRightPinnedCount.
  // This ensures the filler <th> is inserted at the correct position matching the filler <col>.
  const fillerInsertIndex = findFillerInsertIndex(renderCells, props.nonRightPinnedCount);
  const beforeFiller = renderCells.slice(0, fillerInsertIndex);
  const afterFiller = renderCells.slice(fillerInsertIndex);
  const renderCell = (cell: RenderCell): ReactNode => {
    if (cell.type === 'group') {
      return <th key={cell.positionKey} colSpan={cell.colSpan} className="text-xs font-semibold text-gray-500 text-center bg-gray-50 truncate px-2 py-1" style={{
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-content-loader)',
        ...GROUP_HEADER_CELL_BORDER_BOTTOM
      }}>
          {cell.headerName}
        </th>;
    }
    return <th key={cell.columnKey} colSpan={1} className="bg-gray-50" style={{
      position: 'sticky',
      top: 0,
      zIndex: 'var(--z-content-loader)',
      ...GROUP_HEADER_CELL_BORDER_BOTTOM
    }} />;
  };
  return <tr>
      {beforeFiller.map(renderCell)}
      {/* filler th: pairs with filler <col /> between unpinned and right-pinned columns */}
      <th aria-hidden style={{
      position: 'sticky',
      top: 0,
      background: 'white',
      ...GROUP_HEADER_CELL_BORDER_BOTTOM
    }} />
      {afterFiller.map(renderCell)}
      {/* empty action column placeholder — maintains structure alignment with the action header cell */}
      {props.actionColumnWidth ? <th aria-hidden className="p-0 border-0 bg-gray-50" style={{
      position: 'sticky',
      top: 0,
      right: 0,
      zIndex: 'var(--z-content-loader)',
      width: props.actionColumnWidth,
      maxWidth: props.actionColumnWidth,
      ...GROUP_HEADER_CELL_BORDER_BOTTOM
    }}>
          <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
        </th> : null}
    </tr>;
}